import store from '@/store'
import CommonGetters from '@/store/common/common-getters'
import CommonActions from '@/store/common/common-actions'
import Config from '@/constants/config'
import axios from 'axios';
import MemoryService from '@/services/memory.service'
import ObservatoryService from '@/services/observatory.service'
import TimeFrameService from '@/services/timeframe.service'
import SpinnerService from '@/services/spinner.service'
import UtilsService from '@/services/utils.service'
import ObjectsService from '@/services/objects.service'
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import Keys from '@/constants/keys'
import Layers from '@/constants/layers'

import * as THREE from 'three'
import { Text } from 'troika-three-text'
import { Sky } from 'three/examples/jsm/objects/Sky.js'
import SunCalc from 'suncalc'
import { GeoJsonGeometry } from 'three-geojson-geometry'
import HUDService from '@/services/hud.service'
import html2canvas from 'html2canvas'
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry} from 'three/examples/jsm/geometries/TextGeometry.js';
import SyntheticObjectsService from './synthetic-objects.service';
import PopupService from './popup.service';
import * as Spacekit from 'spacekit.js'

const SkychartService = {    
    textureLoader: new THREE.TextureLoader(),
    getBackgroundObjectsApiUrl() {
        return Config.api.restUrl + Config.api.getBackgroundObjects;
    },
    getObjectPathAngleApiUrl() {
        return Config.api.restUrl + Config.api.getObjectPathAngle;
    },
    getTraceObjectApiUrl() {
        return Config.api.restUrl + Config.api.getTraceObject;
    },
    getTraceSyntheticObjectApiUrl() {
        return Config.api.restUrl + Config.api.getTraceSyntheticObject;
    },
    getSettingsOptions(type) {
        return Config.skychart.options[type].split(',');
    },
    getObjectsShow() {
        return store.getters[CommonGetters.skychartObjectsShow];
    },
    setObjectsShow(boolean) {
        store.dispatch(CommonActions.setSkychartObjectsShow, boolean);
    },
    getSkychartMagNumber() {
        return store.getters[CommonGetters.skychartMagNumber];
    },
    setSkychartMagNumber(value) {
        store.dispatch(CommonActions.setSkychartMagNumber, value);
    },
    getActiveObjectName() {
        return store.getters[CommonGetters.skychartActiveObjectName];
    },
    setActiveObjectName(string) {
        store.dispatch(CommonActions.setSkychartActiveObjectName, string);
    },
    getSettingsPanelName() {
        return store.getters[CommonGetters.skychartSettingsPanelName];
    },
    setSettingsPanelName(string) {
        store.dispatch(CommonActions.setSkychartSettingsPanelName, string);
    },
    getScdRoute(){
        return store.getters[CommonGetters.scdRoute];
    },
    setScdRoute(value){
        return store.dispatch(CommonActions.setScdRoute, value);
    },
    getSettings() {
        return store.getters[CommonGetters.skychartSettings];
    },
    setSettings(object) {
        store.dispatch(CommonActions.setSkychartSettings, object);
    },
    getSettingsDefaults() {
        return Config.skychart.settings;
    },   
    setSettingsDefaults() {
        const defaults = SkychartService.getSettingsDefaults();
        SkychartService.setSettings(defaults);
    },
    getPathAngleLimit() {        
        return Config.skychart.settings.pathAngleLimit;
    },       
    datum() {
        const date = new Date(UtilsService.julianToTimestamp(SkychartService.getTraceDate()*1));
        const observatory = ObservatoryService.getSelectedObservatory();
        const MST = UtilsService.getMST(date, observatory.longitude);
        return [date, observatory, MST];
    },
    updateSetting(event) {
        const settings = SkychartService.getSettings();
        const group = event.group;        
        const option = event.change.option;        
        const property = event.change.property;        
        const value = event.change.value;                        
        if (event.change.option)            
            settings[group][option][property] = value;
        else  
            settings[group][property] = value;

        SkychartService.setSettings(settings);
    },
    getInvertColors() {
        const settings = SkychartService.getSettings();
        return settings && settings.invertColors;
    },
    setInvertColors(boolean) {
        const settings = SkychartService.getSettings();
        settings.invertColors = boolean;
        SkychartService.setSettings(settings);
    },
    initAtmosphere(scene, visualiseAtmosphere) {
        const sky = new Sky();
        sky.scale.setScalar(200);
        sky.name = 'Atmosphere';
        sky.visible = visualiseAtmosphere;
        scene.add(sky);

        const uniforms = sky.material.uniforms;
        uniforms['turbidity'].value = 10;
        uniforms['rayleigh'].value = 3;
        uniforms['mieCoefficient'].value = 0.015;
        uniforms['mieDirectionalG'].value = 0.9999;

        const sun = new THREE.Vector3();
        const [date, observatory] = SkychartService.datum();
        const sunPosition = SunCalc.getPosition(date, observatory.latitude, observatory.longitude);
        const sunAlt = sunPosition.altitude;
        const sunAz = sunPosition.azimuth;

        sun.setFromSphericalCoords(1, Math.PI/2 - sunAlt, Math.PI - sunAz);

        uniforms['sunPosition'].value.copy(sun);

        return {
            sunAlt
        }
    },
    initLandscape(scene, visualiseLandscape, sunAlt) {
        SkychartService.textureLoader.load('/static/textures/landscape.png', map => {
            let sunHeight = UtilsService.normalize(0, Math.PI/2)(sunAlt) * 2;
            sunHeight = sunHeight < 0.1 ? 0.1 : sunHeight;
            sunHeight = sunHeight > 1 ? 1 : sunHeight;
            const dimmColor = UtilsService.rgbToHex((255 * sunHeight).toFixed());
            const geometry = new THREE.SphereGeometry(97, 36, 36);
            const material = new THREE.MeshBasicMaterial({ map,
                alphaTest: 0.01,
                transparent: true
             });
            material.color.setHex('0x' + dimmColor);
            const landscape = new THREE.Mesh(geometry, material);
            landscape.material.side = THREE.BackSide;
            landscape.name = 'Landscape';
            landscape.visible = visualiseLandscape;
            landscape.renderOrder = 1;
            scene.add(landscape);
        });
    },
    initMilkyWay(scene, milkyWayVisible) {
        SkychartService.textureLoader.load('/static/textures/milkyway-equatorial.jpg', map => {
            const geometry = new THREE.SphereGeometry(102, 24, 8);
            const material = new THREE.MeshBasicMaterial({ map, transparent: true, opacity: 0.2 });
            const milkyWay = new THREE.Mesh(geometry, material);
            milkyWay.material.side = THREE.BackSide;

            const [, observatory, MST] = SkychartService.datum();

            milkyWay.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
            milkyWay.rotation.y = THREE.MathUtils.degToRad(-MST - 90);
            milkyWay.name = 'MilkyWay';
            milkyWay.visible = milkyWayVisible;
            scene.add(milkyWay);
        });
    },
    initGrids(scene, settings) {
        const fov = SkychartService.getSettings().fov.value;
        const maxFov = SkychartService.getSettings().fov.max;
        const relFov = fov / maxFov;
        
        let magNumber;

        if (relFov < 0.1)
            magNumber = 1;
        else if ((relFov >= 0.1) && (relFov < 0.2))
            magNumber = 2;
        else if ((relFov >= 0.2) && (relFov < 0.5))
            magNumber = 5;
        else if (relFov >= 0.5)
            magNumber = 10;

        //const savedMagNumber = SkychartService.getSkychartMagNumber();

        //if (magNumber !== savedMagNumber[0]){
            SkychartService.setSkychartMagNumber([magNumber, magNumber, magNumber, magNumber]);
            const horizontalGrid = SkychartService.buildGrid(settings, "horizontalGrid", 0x009fff, magNumber, magNumber);
            const equatorialGrid = SkychartService.buildGrid(settings, "equatorialGrid", 0x91c411, magNumber, magNumber);
            const eclipticGrid = SkychartService.buildGrid(settings, "eclipticGrid", 0xFFA200, magNumber, magNumber);       
            scene.add(horizontalGrid);
            scene.add(equatorialGrid);
            scene.add(eclipticGrid);
        //}
    },

    gridColor(name){
        let color;
        if (name == 'horizontalGrid')
            color = 0x009fff;
        else if (name == 'equatorialGrid')
            color = 0x91c411;
        else 
            color = 0xFFA200;
        return color;
    },
    updatePlanetsAndLabels(scene){
        const fov = SkychartService.getSettings().fov.value;
        const maxFov = SkychartService.getSettings().fov.max;
        const scaleFactor = 2 * fov / maxFov; 

        const planetNames = ['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'];
        
        if (SkychartService.getSettings().displaySettings.planets.value && SkychartService.getSettings().displaySettings.planetNames.value){
            for (const idx in planetNames){
                const planetName = planetNames[idx];
                if (scene.getObjectByName('Label_' + planetName)) {              
                    let planetLabel = scene.getObjectByName('Label_' + planetName);            
                    planetLabel.scale.set(scaleFactor, scaleFactor, scaleFactor);
                }
            } 
        }
    },
    updateGrids(scene, settings, name, toggleVisib = false, rotate = false){
        const scd = window['scd'];
        const fov = SkychartService.getSettings().fov.value;
        const maxFov = SkychartService.getSettings().fov.max;
        const relFov = fov / maxFov;
        const scaleFactor = 2 * fov / maxFov; 
        const gridNames = ["horizontalGrid", "equatorialGrid", "eclipticGrid"];
        let equator, compass;
        let magNumber, magNumberVert;
        const [ra, dec, az, alt] = SkychartService.zoomRaDec(scd.controls);/* eslint-disable-line */
        //let [leftLimAz, rightLimAz, topLimAlt, botLimAlt] = this.fovLimits(az, alt, scd.camera);/* eslint-disable-line */

        if (relFov < 0.1)
            magNumber = 1;
        else if ((relFov >= 0.1) && (relFov < 0.2))
            magNumber = 2;
        else if ((relFov >= 0.2) && (relFov < 0.5))
            magNumber = 5;
        else if (relFov >= 0.5)
            magNumber = 10;

        magNumberVert = magNumber;

        compass = scene.getObjectByName('Compass');
        if (compass && compass.children)
        for (const ind in compass.children){
            const child = compass.children[ind];
            if (child.name == "horizontalGridEquator")
                child.scale.set(1, 1, scaleFactor);
            else{
                child.scale.set(scaleFactor, scaleFactor, scaleFactor); 
                const labelPos = new THREE.Vector3();
                labelPos.setFromSphericalCoords(96, THREE.MathUtils.degToRad(90 - 1), THREE.MathUtils.degToRad( 90));
                let height = labelPos.y;
                child.position.y = height*scaleFactor;
            }
        }
                
        for (const idx in gridNames) {
            const gridName = gridNames[idx];
            if (settings[`${gridName}`].value){ 
                let [leftLimAz, rightLimAz, topLimAlt, botLimAlt] = this.rotateMargins(az, alt, gridName);/* eslint-disable-line */
                let altLimit = 70;

                if ((topLimAlt > altLimit || botLimAlt > altLimit)||(topLimAlt < -altLimit || botLimAlt < -altLimit))
                    if (magNumberVert <= 5)
                        magNumberVert = 5;

                altLimit = 80;

                if ((topLimAlt > altLimit || botLimAlt > altLimit)||(topLimAlt < -altLimit || botLimAlt < -altLimit))
                    magNumberVert = 10;

                const savedMagNumber = SkychartService.getSkychartMagNumber();
              
                if (magNumber !== savedMagNumber[idx] || magNumberVert !== savedMagNumber[idx + 3] || rotate){
                    const color = this.gridColor(gridName);
                    savedMagNumber[idx] = magNumber;
                    savedMagNumber[idx + 3] = magNumberVert;
                    SkychartService.setSkychartMagNumber(savedMagNumber);
                    scene.remove(scene.getObjectByName(gridName));
                    const grid = SkychartService.buildGrid(settings, gridName, color, magNumber, magNumberVert);
                    scene.add(grid);
                }

                equator = scene.getObjectByName(gridName + "Equator");
                if (equator)
                    equator.scale.set(1, 1, scaleFactor);

                const grid = scene.getObjectByName(gridName);
                this.findGridScreenIntersections(grid);

                if (grid && grid.children){
                    const labels = grid.children;
                    for (const ind in labels){
                        const label = labels[ind];
                        if (label.text){
                            label.scale.set(scaleFactor, scaleFactor, scaleFactor);
                        }
                    }
                }
            }
        }

        if (toggleVisib){
            const savedMagNumber = SkychartService.getSkychartMagNumber();
            const idx = gridNames.indexOf(name);
            savedMagNumber[idx] = magNumber;
            savedMagNumber[idx + 3] = magNumberVert;
            SkychartService.setSkychartMagNumber(savedMagNumber);
            const existingGrid = scene.getObjectByName(name);
            const existingMagNumber = 1000 * existingGrid.geometry.parameters.thresholdAngle;
            const color = this.gridColor(name);
            if (settings[`${name}`].value && existingMagNumber != magNumber){  
                scene.remove(existingGrid);
                const grid = SkychartService.buildGrid(settings, name, color, magNumber, magNumber);
                if (grid && grid.children){
                    const labels = grid.children;
                    for (const ind in labels){
                        const label = labels[ind];
                        if (label.text){
                            label.scale.set(scaleFactor, scaleFactor, scaleFactor);
                        }
                    }
                }
                scene.add(grid);
            }
        }
    },
    buildGrid(settings, name, color, magNumber, magNumberVert) {
        const geometryHorizontal = new THREE.SphereGeometry(99, 360/magNumberVert, 180/magNumber);
        const edgesHorizontal = new THREE.EdgesGeometry(geometryHorizontal, 0.001 * magNumber);
        const grid = new THREE.LineSegments(edgesHorizontal, new THREE.LineBasicMaterial({ color,
            transparent: true,
            opacity: 0.66
         }));
         
        grid.name = name;
        grid.visible = settings[`${name}`].value;  
        let longitudeLabels = {};
        let latitudeLabels = {};

        longitudeLabels.name = "longitudeLabels";
        latitudeLabels.name = "latitudeLabels";

        for (let i = 0; i <= 360; i += magNumberVert) {
            const longitudeLabel = SkychartService.createLabel(`${i}°`, new THREE.Vector3(0, 0, 99), color);
            longitudeLabel.name = "longitude"; 
            longitudeLabel.visible = false;  
            const longitudeLabelClone = SkychartService.createLabel(`${i}°`, new THREE.Vector3(0, 0, 99), color);
            longitudeLabelClone.name = "longitude"; 
            longitudeLabelClone.visible = false;  
            grid.add(longitudeLabel);
            grid.add(longitudeLabelClone);
        }
        for (let i = 0; i <= 180; i += magNumber) {
            const latitudeLabel = SkychartService.createLabel(`${90 - i}°`, new THREE.Vector3(0, 0, 99), color);
            latitudeLabel.name = "latidude";
            latitudeLabel.visible = false;
            const latitudeLabelClone = SkychartService.createLabel(`${90 - i}°`, new THREE.Vector3(0, 0, 99), color);
            latitudeLabelClone.name = "latidude";
            latitudeLabelClone.visible = false;
            grid.add(latitudeLabel);
            grid.add(latitudeLabelClone);
        }

        if (name !== 'horizontalGrid') {
            const [, observatory, MST] = SkychartService.datum();
            const equator = SkychartService.buildEquator(color, name);
            grid.add(equator);
            let euler = new THREE.Euler(THREE.MathUtils.degToRad(90 - observatory.latitude), THREE.MathUtils.degToRad(180 - MST), 0, 'XYZ'); // Example Euler angles

            grid.rotation.copy(euler);

            if (name === 'eclipticGrid') {
                grid.rotation.z = THREE.MathUtils.degToRad((90 - 66.5607));
            }
        }

        this.findGridScreenIntersections(grid);
        grid.renderOrder = 0;

        return grid;
    },
    getFOVEdges() {
        const scd = window['scd'];
        const [ra, dec, az, alt] = SkychartService.zoomRaDec(scd.controls);/* eslint-disable-line */
        let [leftLimAz, rightLimAz, topLimAlt, botLimAlt] = this.fovLimits(az, alt, scd.camera);/* eslint-disable-line */

        const topRight = this.sphericalToCartesian(rightLimAz, topLimAlt, 1);
        const topLeft = this.sphericalToCartesian(leftLimAz, topLimAlt, 1);
        const botRight = this.sphericalToCartesian(rightLimAz, botLimAlt, 1);
        const botLeft = this.sphericalToCartesian(leftLimAz, botLimAlt, 1);
        
        return [ topRight, topLeft, botRight, botLeft];
    },
    findGridScreenIntersections(grid) {
        const scd = window['scd'];
        const screenSize = {
            widthHalf: scd.renderer.domElement.width / 2,
            heightHalf: scd.renderer.domElement.height / 2
        };

        const [ topRight, topLeft,] = this.getFOVEdges();
        var horizontal =  new THREE.Vector3();
        horizontal.subVectors(topRight, topLeft);
        const camera = scd.camera;
        // Access the linear array of points

        // Define camera's frustum
        var cameraFrustum = new THREE.Frustum();
        var cameraViewProjectionMatrix = new THREE.Matrix4();
        camera.updateMatrix();
        camera.updateMatrixWorld();
        camera.matrixWorldInverse.getInverse(camera.matrixWorld);
        cameraViewProjectionMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
        cameraFrustum.setFromProjectionMatrix(cameraViewProjectionMatrix);

        const points = grid.geometry.attributes.position.array;

        const gridRotation = grid.rotation.clone(); // Store the grid's rotation
        const inverseGridRotation = new THREE.Euler(
            -gridRotation.x,
            -gridRotation.y,
            -gridRotation.z,
            'ZYX'
        );

        for (const ind in grid.children){
            const el = grid.children[ind];
            if (el.text){
                el.used = false;
                el.visible = false;
            } 
        }

        // Iterate over the array, grouping every two consecutive points
        for (let i = 0; i < points.length; i += 6) {
            // Each group of two points represents a line segment
            const startVertex = new THREE.Vector3(points[i], points[i + 1], points[i + 2]);
            const endVertex = new THREE.Vector3(points[i + 3], points[i + 4], points[i + 5]);

            // Convert vertices to world space
            const startWorld = startVertex.clone().applyEuler(gridRotation);
            const endWorld = endVertex.clone().applyEuler(gridRotation);

            if (cameraFrustum.containsPoint(startWorld) || cameraFrustum.containsPoint(endWorld)){


                // Convert vertices to screen space
                const startScreen = startWorld.clone().project(camera);
                const endScreen = endWorld.clone().project(camera);
        
                // Check if the line segment intersects the screen edges
                if (
                    (this.isPointOnScreen(startScreen, screenSize) && !this.isPointOnScreen(endScreen, screenSize)) ||
                    (this.isPointOnScreen(endScreen, screenSize) && !this.isPointOnScreen(startScreen, screenSize))
                ) {

                    var azim, altit;

                    let edge, adjustedPosition;

                    if (this.isPointOnScreen(startScreen, screenSize)){
                        edge = 'start';
                        [azim, altit,] = this.cartesianToSphericalgrid(startVertex);
                        adjustedPosition = this.findIntersPosition(startScreen, endScreen);
                    }
                    else{
                        [azim, altit,] = this.cartesianToSphericalgrid(endVertex);/* eslint-disable-line */
                        edge = 'end';
                        adjustedPosition = this.findIntersPosition(endScreen, startScreen);
                    }
                    adjustedPosition.applyEuler(inverseGridRotation);

                    let label;

                    //Latitude line condition
                    if (startVertex.y == endVertex.y){
                        label = grid.children.find(obj => obj && obj.name !== "longitude" && obj.text == `${Math.round(altit)}°` && obj.used == false);

                        if (label && !label.used) {
                            label.used = true;
                            label.position.copy(adjustedPosition);
                            label.lookAt(camera.position);
                        }
                    }
                    else {
                        if (Math.round(azim) == 0 || Math.round(azim) == -180 || Math.round(azim) == 180) {
                            if (edge == 'start')
                                [azim, altit,] = this.cartesianToSphericalgrid(endVertex);/* eslint-disable-line */ 
                            else
                                [azim, altit,] = this.cartesianToSphericalgrid(startVertex);
                        }

                        label = grid.children.find(obj => obj && obj.name === "longitude" && obj.text == `${Math.round(azim)}°` && obj.used == false);
                        
                        if (label && !label.used){
                            label.used = true;
                            label.position.copy(adjustedPosition);
                            label.lookAt(camera.position);
                            label.rotation.z = label.rotation.z - Math.PI / 2;
                        }                      
                    }
                    
                    let anchors;

                    if (label){
                        label.anchorX = anchors ? anchors[0] : 'center';
                        label.anchorY = anchors ? anchors[1] : 'center';
                        label.visible = true;
                    }
                }
            }
        }
    },
    isPointOnScreen(point, screenSize) {
        // Convert screen coordinates to actual pixel values
        const widthHalf = screenSize.widthHalf;
        const heightHalf = screenSize.heightHalf;
        const pixelX = (point.x * widthHalf) + widthHalf;
        const pixelY = -(point.y * heightHalf) + heightHalf;

       return (
            (pixelX > 0 &&
            pixelX < 2 * widthHalf) &&
            (pixelY > 0 &&
            pixelY < 2 * heightHalf)
        ) ;
    },
    screenToPixel(screenCoord){
        const scd = window['scd'];
        // Convert screen coordinates to actual pixel values
        const widthHalf = scd.renderer.domElement.width / 2;
        const heightHalf = scd.renderer.domElement.height / 2;
        const pixelX = (screenCoord.x * widthHalf) + widthHalf;
        const pixelY = -(screenCoord.y * heightHalf) + heightHalf;
        
        return [pixelX, pixelY];
    },

    adjustedCoords(pixelX, pixelY, screenPosition){
        const scd = window['scd'];
        const screenWidth = scd.renderer.domElement.width;
        const screenHeight = scd.renderer.domElement.height;
        const distanceToLeft = pixelX;
        const distanceToRight = screenWidth - pixelX;
        const distanceToBottom = pixelY;
        const distanceToTop = screenHeight - pixelY;

        // Find the minimum distance to the screen edges
        const minDistance = Math.min(distanceToLeft, distanceToRight, distanceToTop, distanceToBottom);

        // Adjust label position to be at the closest screen margin
        let adjustedX = screenPosition.x;
        let adjustedY = screenPosition.y;
        const offset = 30; // Adjust the offset value as needed

        if (minDistance === distanceToLeft) {
            adjustedX = -1 + offset / screenWidth + 0.05; // Adjust to left edge with offset
        } else if (minDistance === distanceToRight) {
            adjustedX = 1 - offset / screenWidth - 0.1; // Adjust to right edge with offset
        } else if (minDistance === distanceToTop) {
            adjustedY = -1 + offset / screenHeight + 0.1; // Adjust to bottom edge with offset
        } else {
            adjustedY = 1 - offset / screenHeight - 0.05; // Adjust to top edge with offset
        }

        return [adjustedX, adjustedY, screenPosition.z];
    },
    findIntersPosition(startScreen, endScreen){
        const scd = window['scd'];
        const camera = scd.camera;
        const [startX, startY] = this.screenToPixel(startScreen);
        let [adjustedX, adjustedY, adjustedZ]= this.adjustedCoords(startX, startY, startScreen);

        let adjustedX1 = startScreen.x;
        let adjustedY1 = startScreen.y;
        let adjustedX2 = endScreen.x;
        let adjustedY2 = endScreen.y;

        const a = (adjustedY2 - adjustedY1)/(adjustedX2 - adjustedX1);
        const b = adjustedY2 - a*adjustedX2;

        let X, Y,adjustedPosition;

        if (adjustedX != adjustedX1){
            Y = a*adjustedX + b;
            adjustedPosition = new THREE.Vector3(adjustedX, Y, adjustedZ);
        }

        if (adjustedY != adjustedY1){
            X = (adjustedY - b) / a;
            adjustedPosition = new THREE.Vector3(X, adjustedY, adjustedZ);
        }

        // Unproject adjusted position back to 3D space
        adjustedPosition.unproject(camera);
        const resizePos = adjustedPosition.clone().normalize().multiplyScalar(99);
        return resizePos;
    },
    fovLimits(az, alt, camera){
        let container = document.querySelector('#skychart');

        const width = container.clientWidth;
        const height = container.clientHeight;
        const aspect = width / height;
        
        const horizontalFov = (camera.getEffectiveFOV() * aspect).toFixed(1);
        //const verticalFov = camera.getEffectiveFOV().toFixed(1);

        const hfov = parseFloat(horizontalFov);
        //const vfov = parseFloat(verticalFov); 

        const magCoef = 1.2;

        let leftLimAz = az - hfov/2*magCoef;
        let rightLimAz = az + hfov/2*magCoef;
        const topLimAlt = alt + hfov/2*magCoef;
        const botLimAlt = alt - hfov/2*magCoef;

        return [leftLimAz, rightLimAz, topLimAlt, botLimAlt];
    },
    multiplyMatrixVector(matrix, vector) {
        if (matrix.length !== 3 || matrix[0].length !== 3 || matrix[1].length !== 3 || matrix[2].length !== 3) {
            throw new Error('Invalid matrix size. Must be a 3x3 matrix.');
        }
    
        if (!(vector instanceof THREE.Vector3)) {
            throw new Error('Invalid vector type. Must be a Three.js Vector3.');
        }
    
        const result = new THREE.Vector3();
    
        result.x = matrix[0][0] * vector.x + matrix[0][1] * vector.y + matrix[0][2] * vector.z;
        result.y = matrix[1][0] * vector.x + matrix[1][1] * vector.y + matrix[1][2] * vector.z;
        result.z = matrix[2][0] * vector.x + matrix[2][1] * vector.y + matrix[2][2] * vector.z;
    
        return result;
    },
    rotateMargins(az, alt, name){

        const scd = window['scd'];
        const camera = scd.camera;
        
        let [leftLimAz, rightLimAz, topLimAlt, botLimAlt] = this.fovLimits(az, alt, camera);

        const [, observatory, MST] = SkychartService.datum();
        const rotMatrixA = [
            [1, 0, 0],
            [0, Math.cos(THREE.MathUtils.degToRad(90 - observatory.latitude)), -Math.sin(THREE.MathUtils.degToRad(90 - observatory.latitude))],
            [0, Math.sin(THREE.MathUtils.degToRad(90 - observatory.latitude)), Math.cos(THREE.MathUtils.degToRad(90 - observatory.latitude))]
        ];
        
        const rotMatrixB = [
            [Math.cos(THREE.MathUtils.degToRad(MST)), -Math.sin(THREE.MathUtils.degToRad(MST)), 0],
            [Math.sin(THREE.MathUtils.degToRad(MST)), Math.cos(THREE.MathUtils.degToRad(MST)), 0],
            [0, 0, 1]
        ];

        const unitMatrix = [
            [1, 0, 0],
            [0, 1, 0],
            [0, 0, 1]
        ];
        const rotMatrixC = [
            [Math.cos(THREE.MathUtils.degToRad(90 + 66.5607)), 0, -Math.sin(THREE.MathUtils.degToRad(90 + 66.5607))],
            [0, 1, 0],
            [Math.sin(THREE.MathUtils.degToRad(90 + 66.5607)), 0, Math.cos(THREE.MathUtils.degToRad(90 + 66.5607))]
        ];

        if (name !== "" && name !== 'horizontalGrid') {    
            let [az0, alt0] = this.rotateAngles(az, alt, rotMatrixA, rotMatrixB); /* eslint-disable-line */
            [leftLimAz, rightLimAz, topLimAlt, botLimAlt] = this.fovLimits(az0, alt0, camera);
  
            if (name === 'eclipticGrid') { 
                let [ecaz0, ecalt0] = this.rotateAngles(az0, alt0, unitMatrix, rotMatrixC); /* eslint-disable-line */
                [leftLimAz, rightLimAz, topLimAlt, botLimAlt] = this.fovLimits(ecaz0, ecalt0, camera);
            }
            
        }

        return [leftLimAz, rightLimAz, topLimAlt, botLimAlt];
    },
    createLabel(text, position, color) {

        const label = new Text();
        label.text = text;
        label.fontSize = 1.7;
        label.color = color;
        label.lineHeight = 1.5;
        label.position.set(position);

        return label;
    },
    buildEquator(color, name) {

        const geometry = new THREE.TorusGeometry(98, 0.2, 3, 72);
        const material = new THREE.MeshBasicMaterial({ color });
        const equator = new THREE.Mesh(geometry, material);

        equator.name = name + "Equator";
        equator.rotation.x = Math.PI / 2;
        return equator;
    },
    initConstellations(scene, constellationsVisible) {
        const [, observatory, MST] = SkychartService.datum();

        const getLines = new Promise((resolve, reject) => {
            fetch('/static/json/constellations-lines.json')
                .then(res => resolve(res.json()))
                .catch(error => reject(error.message));
        });

        const getNames = new Promise((resolve, reject) => {
            fetch('/static/json/constellations.json')
                .then(res => resolve(res.json()))
                .catch(error => reject(error.message));
        });

        Promise.all([getLines, getNames]).then(([lines, names]) => {
            const group = new THREE.Group();
            const radius = 98;

            lines.features.forEach(({ geometry }) => {
                const constellationLine = new THREE.LineSegments(new GeoJsonGeometry(geometry, radius), new THREE.LineBasicMaterial({ color: 0x0F84DD }));
                group.add(constellationLine);
            });

            names.features.forEach(({ properties, geometry }) => {
                const geoJsonGeometry = new GeoJsonGeometry(geometry, radius);
                const coordinates = geoJsonGeometry.parameters.geoJson.coordinates;
                const constellationLabel = new Text();
                constellationLabel.text = properties.en.toUpperCase();
                constellationLabel.color = 0x0F84DD;
                constellationLabel.defaultFontSize = 1.5;
                constellationLabel.fontSize = 1.5;
                constellationLabel.name = 'SkychartLabel_constellation';
                constellationLabel.letterSpacing = 0.5;
                constellationLabel.anchorX = 'center';
                constellationLabel.anchorY = 'middle';
                constellationLabel.layers.set(Layers.SCD.CONSTELLATIONS);
                constellationLabel.sizeAttenuation = false;
       
                constellationLabel.position.setFromSphericalCoords(radius + 2, THREE.MathUtils.degToRad(90 - coordinates[1]), THREE.MathUtils.degToRad(coordinates[0]));
                group.add(constellationLabel);
            });
            
            group.name = 'Constellations';
            group.visible = constellationsVisible;
            group.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
            group.rotation.y = THREE.MathUtils.degToRad(-MST - 180);
            scene.add(group);
        });
    },
    initRegions(scene, regionsVisible) {
        fetch('/static/json/constellations-borders.json')
            .then(res => res.json())
            .then(regions => {
                const radius = 98;
                const [, observatory, MST] = SkychartService.datum();
                const group = new THREE.Group();

                group.name = 'Regions';
                group.visible = regionsVisible;
                group.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
                group.rotation.y = THREE.MathUtils.degToRad(-MST - 180);
                regions.features.forEach(({ geometry }) => {
                    const borderGeometry = new THREE.LineSegments(new GeoJsonGeometry(geometry, radius), new THREE.LineBasicMaterial({ color: 0x555555 }));
                    group.add(borderGeometry)
                });
                scene.add(group);
            })
    },
    initDsos(scene, dsosVisible) {
        fetch('/static/json/dsos.bright.json')
            .then(res => res.json())
            .then(dsos => {
                const radius = 98;
                const [, observatory, MST] = SkychartService.datum();
                const group = new THREE.Group();

                group.name = 'Dsos';
                group.visible = dsosVisible;
                group.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
                group.rotation.y = THREE.MathUtils.degToRad(-MST - 180);

                dsos.features.forEach(({ properties, geometry }) => {
                    const geoJsonGeometry = new GeoJsonGeometry(geometry, radius);
                    const coordinates = geoJsonGeometry.parameters.geoJson.coordinates;
                    const dsosRegion = new THREE.Points(geoJsonGeometry, new THREE.PointsMaterial({ color: 0x0ECB00, size: 1.5 }));
                    group.add(dsosRegion)

                    const dsosLabel = new Text();
                    dsosLabel.text = properties.desig.toUpperCase() + '\n';
                    dsosLabel.color = 0x0ECB00;
                    dsosLabel.defaultFontSize = 1.5;
                    dsosLabel.fontSize = 1.5;
                    dsosLabel.lineHeight = 0.75;
                    dsosLabel.name = 'SkychartLabel_dsos';
                    dsosLabel.anchorX = 'center';
                    dsosLabel.anchorY = 'bottom';
                    dsosLabel.layers.set(Layers.SCD.DSOS);
                    dsosLabel.position.setFromSphericalCoords(radius + 2, THREE.MathUtils.degToRad(90 - coordinates[1]), THREE.MathUtils.degToRad(coordinates[0]));
                    group.add(dsosLabel)
                });                
                scene.add(group);
            })
    },
    initCompass(scene, compassVisible) {
        const loader = new FontLoader();
        const compass = new THREE.Group();
        compass.name = 'Compass';
        compass.visible = compassVisible;
        if (loader)
        loader.load('/static/fonts/helvetiker_regular.typeface.json', font => {
            const directions = ['N', 'W', 'S', 'E'];
            const material = new THREE.MeshBasicMaterial({ color: 0x009fff });

            for (const [index, direction] of directions.entries()) {
                const geometry = new TextGeometry(direction, { font, size: 3, height: 0, curveSegments: 12 });
                const letter = new THREE.Mesh(geometry, material);
                letter.position.setFromSphericalCoords(96, THREE.MathUtils.degToRad(90 - 1), THREE.MathUtils.degToRad(index * 90));

                if (index === 0) letter.rotation.y = Math.PI;
                if (index === 1) letter.rotation.y = Math.PI / -2;
                if (index === 2) letter.rotation.y = 2 * Math.PI;
                if (index === 3) letter.rotation.y = Math.PI / 2;

                compass.add(letter);
            }
            const horizontalEquator = SkychartService.buildEquator(0x009fff, "horizontalGrid");
            compass.add(horizontalEquator);
            scene.add(compass);
        });
    },
    initBackgroundObjects(scene, mag) {
        const magFaint = SkychartService.getSettings().mag.value;
        const paramsObj = {
            dec: 0,
            magBright: 0,
            magFaint,
            ra: 0.001,
            sizeDec: 8,
            sizeRa: 8
        }

        const julianDates = TimeFrameService.getJulianDates();
        paramsObj.julianDate = julianDates['julianDateStart'];

        const utcDates = TimeFrameService.getUtcDates();
        paramsObj.utcStart = utcDates['utcStart'];

        const observatory = ObservatoryService.getSelectedObservatory();
        if (observatory.code) {
            paramsObj.observatoryCode = observatory.code;
        } else {
            paramsObj.observatoryElevation = ObservatoryService.getObservatoryElevation();
            paramsObj.observatoryLatitude = observatory.latitude;
            paramsObj.observatoryLongitude = observatory.longitude*1 < 0 ? observatory.longitude*1 + 360 : observatory.longitude;
        }

        const url = SkychartService.getBackgroundObjectsApiUrl() + '/rectangle' + UtilsService.parseParams(paramsObj);

        const recentObjectData = MemoryService.getSessionStorageEntry(url);
        if (recentObjectData && recentObjectData.length) {
            SkychartService.buildBackgroundObjects(scene, recentObjectData, mag);
        } else {
            axios(url)
                .then(response => {
                    MemoryService.setSessionStorageEntry(url, response.data.data);
                    SkychartService.buildBackgroundObjects(scene, response.data.data, mag);
                });
        }
    },

    initZoomObjects(scene, fov, controls) {
        if (fov > 30) {
            window['scd'].zoomObjects = [];
            while (scene.getObjectByName('BackgroundObjectsHighResolution'))
                scene.remove(scene.getObjectByName('BackgroundObjectsHighResolution'));

            SkychartService.updateSetting({
                group: 'mag',
                change: {
                    property: 'value',
                    value: 7,
                }
            });
        }
        
        if (fov > 3) {
            return;
        }
        
        const [ra, dec] = SkychartService.zoomRaDec(controls);

        let magFaint;
        let size;
        let type = 'rectangle';
        const magFaintMax = 22;
        
        if (fov > 1.5) {
            magFaint = magFaintMax - 10;
            size = 4;
        } else if (fov > 1) {
            magFaint = magFaintMax - 7.5;
            size = 2;
        } else if (fov > 0.5) {
            magFaint = magFaintMax - 5;
            size = 1;
        } else if (fov > 0.2) {
            magFaint = magFaintMax - 2.5;
            size = 0.5;
        } else {
            magFaint = magFaintMax;
            size = 0.25;
            type = 'circle';
        }

        const paramsObj = {
            dec,
            magBright: 7,
            magFaint,
            ra,
        }

        if (type === 'rectangle') {
            paramsObj.sizeRa = size;
            paramsObj.sizeDec = size;
        } else {
            paramsObj.radius = size;
        }

        const julianDates = TimeFrameService.getJulianDates();
        paramsObj.julianDate = julianDates['julianDateStart'];

        const utcDates = TimeFrameService.getUtcDates();
        paramsObj.utcStart = utcDates['utcStart'];

        const observatory = ObservatoryService.getSelectedObservatory();
        if (observatory.code) {
            paramsObj.observatoryCode = observatory.code;
        } else {
            paramsObj.observatoryElevation = ObservatoryService.getObservatoryElevation();
            paramsObj.observatoryLatitude = observatory.latitude;
            paramsObj.observatoryLongitude = observatory.longitude*1 < 0 ? observatory.longitude*1 + 360 : observatory.longitude;
        }

        const url = SkychartService.getBackgroundObjectsApiUrl() + '/' + type + UtilsService.parseParams(paramsObj);
        axios(url)
            .then(response => {
                const currentZoomObjects = response.data.data;
                const allZoomObjects = window['scd'].zoomObjects;
                let zoomObjectsToAdd = currentZoomObjects;
                if (allZoomObjects && allZoomObjects.length) {
                    zoomObjectsToAdd = zoomObjectsToAdd.filter(e => !allZoomObjects.find(el => el[0] === e[0]));
                }
                window['scd'].zoomObjects = window['scd'].zoomObjects.concat(zoomObjectsToAdd);
                SkychartService.buildBackgroundObjects(scene, zoomObjectsToAdd, 20, 'BackgroundObjectsHighResolution');
                SkychartService.updateSetting({
                    group: 'mag',
                    change: {
                        property: 'value',
                        value: magFaint
                    }
                });
            });
    },
    initTraceObjects(scene, objects, camera) {
        SkychartService.setLoadedObjects([]);
        SkychartService.setExceedPathAngleObjects([]);

        const paramsObj = {
            kev: 0.172,
            vZenith: 21.817
        }

        const julianDates = TimeFrameService.getJulianDates();
        paramsObj.julianDateStart = julianDates['julianDateStart'];
        paramsObj.julianDateEnd = julianDates['julianDateEnd'];

        const utcDates = TimeFrameService.getUtcDates();
        paramsObj.utcStart = utcDates['utcStart'];
        paramsObj.utcEnd = utcDates['utcEnd'];

        const observatory = ObservatoryService.getSelectedObservatory();
        if (observatory.code) {
            paramsObj.observatoryCode = observatory.code;
        } else {
            paramsObj.observatoryElevation = ObservatoryService.getObservatoryElevation();
            paramsObj.observatoryLatitude = observatory.latitude;
            paramsObj.observatoryLongitude = observatory.longitude*1 < 0 ? observatory.longitude*1 + 360 : observatory.longitude;
        }

        const timeStep = TimeFrameService.getTimestep();
        paramsObj.step = timeStep.converted;

        for (const object of objects) {
            const designator = object.designator ? object.designator : object.number ? object.number : object.name;            
            paramsObj.name = designator;

            if (object.designator) {
                paramsObj.kep = true;
                paramsObj.kep_epoch = object.referenceEpoch;
                paramsObj.kep_a = (object.aphelionDistance + object.perihelionDistance) / 2;
                paramsObj.kep_e = (object.aphelionDistance - object.perihelionDistance)/(object.aphelionDistance + object.perihelionDistance);
                paramsObj.kep_i = object.inclination;
                paramsObj.kep_long = object.rightAscension;
                paramsObj.kep_omega = object.perihelionArgument;
                paramsObj.kep_mean = object.meanAnomaly;
                paramsObj.mag = object.absoluteMagnitude;
                paramsObj.slope = object.slopeParameter;
            }
                        
            const urlPathAngle = !object.designator ? SkychartService.getObjectPathAngleApiUrl() + UtilsService.parseParams(paramsObj) : null;
            const url = (!object.designator ? SkychartService.getTraceObjectApiUrl() : SkychartService.getTraceSyntheticObjectApiUrl()) + UtilsService.parseParams(paramsObj);
            const recentPathAngleData = urlPathAngle ? MemoryService.getSessionStorageEntry(urlPathAngle) : true;            
            const recentObjectData = MemoryService.getSessionStorageEntry(url);            
                                    
            if (!recentPathAngleData && !ObjectsService.isComet(object)) {                                    
                 axios(urlPathAngle)
                    .then(response => {       
                        if (!response.data || !response.data.object.data) return;                                                                                                
                        MemoryService.setSessionStorageEntry(urlPathAngle, response.data.object);                                     
                        const arclength = response.data.object.data[0].arclength;                                                
                        if (arclength && (arclength >= SkychartService.getPathAngleLimit())) {                        
                            SkychartService.addExceedPathAngleObject(object.name);                            
                        }                        
                    })
            }            

            if (recentObjectData) {
                SkychartService.buildTraceObject(scene, recentObjectData, object.designator || object.name, camera.fov);
            } else {
                axios(url)
                    .then(response => {
                        if (!response.data || !response.data.object) return;
                        SkychartService.buildStarlightAffected(response.data.object);
                        MemoryService.setSessionStorageEntry(url, response.data.object);
                        SkychartService.buildTraceObject(scene, response.data.object, object.designator || object.name, camera.fov);
                    })
                    .catch(error => {
                        console.log(error);
                        PopupService.show({
                            component: 'PopupInfo',
                            type: 'error',
                            message: Keys.t.errorDuringApiCall
                        })
                    })
            }
        }
    },

    loadTexture(url) {
        return new Promise((resolve, reject) => {
            const loader = new THREE.TextureLoader();
            loader.load(url, resolve, undefined, reject);
        });
    },

    async buildMoon(scene){  
        const scd = window['scd'];
        const camera = scd.camera;

        const [date,observatory,] = SkychartService.datum();
        
        const objectData = SkychartService.getTraceObjects();
        const jd = parseFloat(SkychartService.getTraceDate());
        let moonRa, moonDec;

        // Get the first object name
        const firstObjectName = Object.keys(objectData)[0];

        if (objectData[firstObjectName] && objectData[firstObjectName].data)
        for (const [, trial] of objectData[firstObjectName].data[0].data.entries()) {
            if (jd == parseFloat(trial[0])){
                moonRa = parseFloat(trial[24]) * Math.PI/180;
                moonDec = parseFloat(trial[25]) * Math.PI/180; 
            }
        }

        const observatoryCoordinates = [observatory.latitude, observatory.longitude];
        const [moonAlt, moonAz] = UtilsService.convertToAltitudeAndAzimuth(date, [moonRa*180/Math.PI, moonDec*180/Math.PI], observatoryCoordinates);

        let posMoon = new THREE.Vector3();

        let firstRotation = new THREE.Euler(
            THREE.MathUtils.degToRad(-90),
            THREE.MathUtils.degToRad(0),
            THREE.MathUtils.degToRad(-90),
            'XYZ'
        );

        posMoon.x = Math.cos(moonAlt*Math.PI/180) * Math.cos(-moonAz*Math.PI/180);
        posMoon.y = Math.cos(moonAlt*Math.PI/180) * Math.sin(-moonAz*Math.PI/180);
        posMoon.z = Math.sin(moonAlt*Math.PI/180);


        posMoon.applyEuler(firstRotation);

        let moon;

        if (scene.getObjectByName('Moon')){
            moon = scene.getObjectByName('Moon');
        }
        else 
        {
            const moon_texture = new THREE.TextureLoader().load('/static/textures/detailed/moon.jpg');
            const moon_geometry = new THREE.SphereGeometry(0.5, 32, 32);
            const moon_material = new THREE.MeshStandardMaterial({ map: moon_texture }); // Ensure the material is light-responsive
            moon = new THREE.Mesh(moon_geometry, moon_material);
            moon.name = 'Moon';
            scene.add(moon);
        }
        posMoon.normalize().multiplyScalar(101);
        moon.rotation.set(0, 0, 0);
        moon.position.set(posMoon.x, posMoon.y, posMoon.z);

        const origin = new THREE.Vector3(0, 0, 0);
        moon.lookAt(origin);

        const moonRotation = moon.rotation.clone(); // or use moon.quaternion

        // Assuming the shift angle and axis (e.g., the Y-axis)
        const shiftAngle = Math.PI/2; // Replace with your actual shift angle
 
        // Using Quaternion
        const shiftQuaternion = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), -shiftAngle);
        const moonQuaternion = new THREE.Quaternion().setFromEuler(moonRotation);
        moonQuaternion.multiply(shiftQuaternion);
        moon.quaternion.copy(moonQuaternion);


        moon.layers.set(Layers.SCD.MOON);

        const sunPosition = SunCalc.getPosition(date, observatory.latitude, observatory.longitude);
        const sunAlt = sunPosition.altitude;
        const sunAz = sunPosition.azimuth; 
        const sunVector = new THREE.Vector3();

        sunVector.setFromSphericalCoords(1, Math.PI/2 - sunAlt, Math.PI - sunAz);

        sunVector.normalize();

        sunVector.multiplyScalar(10000000);

        
        // Add a directional light to simulate sunlight
        let sunLight;
        if (scene.getObjectByName('SunLight')) {
            sunLight = scene.getObjectByName('SunLight');
        } else {
            sunLight = new THREE.DirectionalLight(0xffffff, 1.5);
            sunLight.name = 'SunLight';
            scene.add(sunLight);
        }
        sunLight.position.copy(sunVector); // Position the light based on the sun's position
        sunLight.target = moon; // Ensure the light points towards the moon

        let moonLabel;

        if (scene.getObjectByName('Label_Moon')){
            moonLabel = scene.getObjectByName('Label_Moon');
        }
        else
        {
            moonLabel = HUDService.createPlanetLabel('Moon', scene);
        }
        moonLabel.position.copy(posMoon.normalize().multiplyScalar(100));
        moonLabel.fontSize = SkychartService.getSettings().displaySettings.moonNameSize.value;
        moonLabel.color = SkychartService.getSettings().displaySettings.moonName.color;
        moonLabel.layers.set(Layers.SCD.MOON_NAME);

        if (moonLabel)
            moonLabel.lookAt(camera.position);  
    },

    async buildPlanets(scene){  

        const scd = window['scd'];
        const camera = scd.camera;

        const [, observatory, MST] = SkychartService.datum(); /* eslint-disable-line */
    

        const ephMercury = Spacekit.SpaceObjectPresets.MERCURY.ephem;
        const ephVenus = Spacekit.SpaceObjectPresets.VENUS.ephem;
        const ephEarth = Spacekit.SpaceObjectPresets.EARTH.ephem;
        const ephMars = Spacekit.SpaceObjectPresets.MARS.ephem;
        const ephJupiter = Spacekit.SpaceObjectPresets.JUPITER.ephem;
        const ephSaturn = Spacekit.SpaceObjectPresets.SATURN.ephem;
        const ephUranus = Spacekit.SpaceObjectPresets.URANUS.ephem;
        const ephNeptune = Spacekit.SpaceObjectPresets.NEPTUNE.ephem;
   

        const jd = parseFloat(SkychartService.getTraceDate());
        let posMercury = SkychartService.getPositionAtTimeElliptical(jd, ephMercury);
        let posVenus = SkychartService.getPositionAtTimeElliptical(jd, ephVenus);
        let posEarth = SkychartService.getPositionAtTimeElliptical(jd, ephEarth);
        let posMars = SkychartService.getPositionAtTimeElliptical(jd, ephMars);
        let posJupiter = SkychartService.getPositionAtTimeElliptical(jd, ephJupiter);
        let posSaturn = SkychartService.getPositionAtTimeElliptical(jd, ephSaturn);
        let posUranus = SkychartService.getPositionAtTimeElliptical(jd, ephUranus);
        let posNeptune = SkychartService.getPositionAtTimeElliptical(jd, ephNeptune);

    
        let firstRotation = new THREE.Euler(
            THREE.MathUtils.degToRad(-90),
            THREE.MathUtils.degToRad(0),
            THREE.MathUtils.degToRad(-90),
            'XYZ'
        );

        posMercury.applyEuler(firstRotation);
        posVenus.applyEuler(firstRotation);
        posEarth.applyEuler(firstRotation);
        posMars.applyEuler(firstRotation);
        posJupiter.applyEuler(firstRotation);
        posSaturn.applyEuler(firstRotation);
        posUranus.applyEuler(firstRotation);
        posNeptune.applyEuler(firstRotation);


        posMercury = posMercury.clone().sub(posEarth);
        posVenus = posVenus.clone().sub(posEarth);
        posMars = posMars.clone().sub(posEarth);
        posJupiter = posJupiter.clone().sub(posEarth);
        posSaturn = posSaturn.clone().sub(posEarth);
        posUranus = posUranus.clone().sub(posEarth);
        posNeptune = posNeptune.clone().sub(posEarth);

        let secondRotation = new THREE.Euler(
            THREE.MathUtils.degToRad(90 - observatory.latitude),
            THREE.MathUtils.degToRad(180 - MST),
            THREE.MathUtils.degToRad(90 - 66.5607),
            'XYZ'
        );
        
        posMercury.applyEuler(secondRotation);
        posVenus.applyEuler(secondRotation);
        posMars.applyEuler(secondRotation);
        posJupiter.applyEuler(secondRotation);
        posSaturn.applyEuler(secondRotation);
        posUranus.applyEuler(secondRotation);
        posNeptune.applyEuler(secondRotation);


        //the average visual magnitude in seconds
        const merc_ref_rad = 9;
        const ven_ref_rad = 38;
        const mars_ref_rad = 14;
        const jup_ref_rad = 39;
        const sat_ref_rad = 17;
        const ura_ref_rad = 7;
        const nep_ref_rad = 2;

        const jup_size = 0.3;

        let mars;

        if (scene.getObjectByName('Mars')){
            mars = scene.getObjectByName('Mars');
        }
        else 
        {
            let mars_texture = await this.loadTexture('/static/textures/detailed/mars.jpg');
            const mars_size = jup_size*mars_ref_rad/jup_ref_rad;
            const mars_geometry = new THREE.SphereGeometry(mars_size, 32, 32);
            const mars_material = new THREE.MeshBasicMaterial({ map: mars_texture });
            mars = new THREE.Mesh(mars_geometry, mars_material);
            mars.name = 'Mars';
            scene.add(mars);                       
        }

        
        posMars.normalize().multiplyScalar(101);
        mars.position.set(posMars.x, posMars.y, posMars.z);
        mars.layers.set(Layers.SCD.PLANETS);
        

        let marsLabel;
        if (scene.getObjectByName('Label_Mars')){
            marsLabel = scene.getObjectByName('Label_Mars');
        }
        else
        {
            marsLabel = HUDService.createPlanetLabel('Mars', scene);
        }
        marsLabel.position.copy(posMars.normalize().multiplyScalar(100));
        marsLabel.fontSize = SkychartService.getSettings().displaySettings.planetNamesSize.value;
        marsLabel.color = SkychartService.getSettings().displaySettings.planetNames.color;
        marsLabel.layers.set(Layers.SCD.PLANET_NAMES);
        
        if (marsLabel)
            marsLabel.lookAt(camera.position);

        let venus;

        if (scene.getObjectByName('Venus')){
            venus = scene.getObjectByName('Venus');
        }
        else{
            let venus_texture = await this.loadTexture('/static/textures/detailed/venus.jpg');
            const venus_size = jup_size*ven_ref_rad/jup_ref_rad;
            const venus_geometry = new THREE.SphereGeometry(venus_size, 32, 32);
            const venus_material = new THREE.MeshBasicMaterial({ map: venus_texture });
            venus = new THREE.Mesh(venus_geometry, venus_material);
            venus.name = 'Venus';
            scene.add(venus);
        }
        posVenus.normalize().multiplyScalar(101);
        venus.position.set(posVenus.x, posVenus.y, posVenus.z);
        venus.layers.set(Layers.SCD.PLANETS);

        let venusLabel;
        if (scene.getObjectByName('Label_Venus')){
            venusLabel = scene.getObjectByName('Label_Venus');
        }
        else
        {
            venusLabel = HUDService.createPlanetLabel('Venus', scene);
        }
        venusLabel.position.copy(posVenus.normalize().multiplyScalar(100));
        venusLabel.fontSize = SkychartService.getSettings().displaySettings.planetNamesSize.value;
        venusLabel.color = SkychartService.getSettings().displaySettings.planetNames.color;
        venusLabel.layers.set(Layers.SCD.PLANET_NAMES);
        
        if (venusLabel)
            venusLabel.lookAt(camera.position);


        let jupiter;

        if (scene.getObjectByName('Jupiter')){
            jupiter = scene.getObjectByName('Jupiter');
        }
        else 
        {
            let jupiter_texture = await this.loadTexture('/static/textures/detailed/jupiter.jpg');
            const jupiter_geometry = new THREE.SphereGeometry(jup_size, 32, 32);
            const jupiter_material = new THREE.MeshBasicMaterial({ map: jupiter_texture });
            jupiter = new THREE.Mesh(jupiter_geometry, jupiter_material);
            jupiter.name = 'Jupiter';
            scene.add(jupiter);
        }
        posJupiter.normalize().multiplyScalar(101);
        jupiter.position.set(posJupiter.x, posJupiter.y, posJupiter.z);
        jupiter.layers.set(Layers.SCD.PLANETS);

        let jupiterLabel;
        if (scene.getObjectByName('Label_Jupiter')){
            jupiterLabel = scene.getObjectByName('Label_Jupiter');
        }
        else
        {
            jupiterLabel = HUDService.createPlanetLabel('Jupiter', scene);
        }
        jupiterLabel.position.copy(posJupiter.normalize().multiplyScalar(100));
        jupiterLabel.fontSize = SkychartService.getSettings().displaySettings.planetNamesSize.value;
        jupiterLabel.color = SkychartService.getSettings().displaySettings.planetNames.color;
        jupiterLabel.layers.set(Layers.SCD.PLANET_NAMES);
        
        if (jupiterLabel)
            jupiterLabel.lookAt(camera.position);

        let saturn;

        if (scene.getObjectByName('Saturn')){
            saturn = scene.getObjectByName('Saturn');
        }
        else 
        {
            let saturn_texture = await this.loadTexture('/static/textures/detailed/saturn.jpg');
            const saturn_size = jup_size*sat_ref_rad/jup_ref_rad;
            const saturn_geometry = new THREE.SphereGeometry(saturn_size, 32, 32);
            const saturn_material = new THREE.MeshBasicMaterial({ map: saturn_texture });
            saturn = new THREE.Mesh(saturn_geometry, saturn_material);
            saturn.name = 'Saturn';
            scene.add(saturn);
        }
        posSaturn.normalize().multiplyScalar(101);
        saturn.position.set(posSaturn.x, posSaturn.y, posSaturn.z);
        saturn.layers.set(Layers.SCD.PLANETS);

        let saturnLabel;
        
        if (scene.getObjectByName('Label_Saturn')){
            saturnLabel = scene.getObjectByName('Label_Saturn');
        }
        else
        {
            saturnLabel = HUDService.createPlanetLabel('Saturn', scene);
        }
        saturnLabel.position.copy(posSaturn.normalize().multiplyScalar(100));
        saturnLabel.fontSize = SkychartService.getSettings().displaySettings.planetNamesSize.value;
        saturnLabel.color = SkychartService.getSettings().displaySettings.planetNames.color;
        saturnLabel.layers.set(Layers.SCD.PLANET_NAMES);
        
        if (saturnLabel)
            saturnLabel.lookAt(camera.position);

        let uranus;

        if (scene.getObjectByName('Uranus')){
            uranus = scene.getObjectByName('Uranus');
        }
        else 
        {
            let uranus_texture = await this.loadTexture('/static/textures/detailed/uranus.jpg');
            const uranus_size = jup_size*ura_ref_rad/jup_ref_rad;
            const uranus_geometry = new THREE.SphereGeometry(uranus_size, 32, 32);
            const uranus_material = new THREE.MeshBasicMaterial({ map: uranus_texture });
            uranus = new THREE.Mesh(uranus_geometry, uranus_material);
            uranus.name = 'Uranus';
            scene.add(uranus);
        }
        posUranus.normalize().multiplyScalar(101);
        uranus.position.set(posUranus.x, posUranus.y, posUranus.z);
        uranus.layers.set(Layers.SCD.PLANETS);

        let uranusLabel;

        if (scene.getObjectByName('Label_Uranus')){
            uranusLabel = scene.getObjectByName('Label_Uranus');
        }
        else
        {
            uranusLabel = HUDService.createPlanetLabel('Uranus', scene);
        }
        uranusLabel.position.copy(posUranus.normalize().multiplyScalar(100));
        uranusLabel.fontSize = SkychartService.getSettings().displaySettings.planetNamesSize.value;
        uranusLabel.color = SkychartService.getSettings().displaySettings.planetNames.color;
        uranusLabel.layers.set(Layers.SCD.PLANET_NAMES);

        if (uranusLabel)
            uranusLabel.lookAt(camera.position);

        let neptune;

        if (scene.getObjectByName('Neptune')){
            neptune = scene.getObjectByName('Neptune');
        }
        else 
        {
            let neptune_texture = await this.loadTexture('/static/textures/detailed/neptune.jpg');
            const neptune_size = jup_size*nep_ref_rad/jup_ref_rad;
            const neptune_geometry = new THREE.SphereGeometry(neptune_size, 32, 32);
            const neptune_material = new THREE.MeshBasicMaterial({ map: neptune_texture });
            neptune = new THREE.Mesh(neptune_geometry, neptune_material);
            neptune.name = 'Neptune';
            scene.add(neptune);
        }
        posNeptune.normalize().multiplyScalar(101);
        neptune.position.set(posNeptune.x, posNeptune.y, posNeptune.z);
        neptune.layers.set(Layers.SCD.PLANETS);

        let neptuneLabel;
    
        if (scene.getObjectByName('Label_Neptune')){
            neptuneLabel = scene.getObjectByName('Label_Neptune');
        }
        else
        {
            neptuneLabel = HUDService.createPlanetLabel('Neptune', scene);
        }
        neptuneLabel.position.copy(posNeptune.normalize().multiplyScalar(100));
        neptuneLabel.fontSize = SkychartService.getSettings().displaySettings.planetNamesSize.value;
        neptuneLabel.color = SkychartService.getSettings().displaySettings.planetNames.color;
        neptuneLabel.layers.set(Layers.SCD.PLANET_NAMES);
    
        if (neptuneLabel)
            neptuneLabel.lookAt(camera.position);


        let mercury;

        if (scene.getObjectByName('Mercury')){
            mercury = scene.getObjectByName('Mercury');
        }
        else 
        {
            let mercury_texture = await this.loadTexture('/static/textures/detailed/mercury.jpg');
            const mercury_size = jup_size*merc_ref_rad/jup_ref_rad;
            const mercury_geometry = new THREE.SphereGeometry(mercury_size, 32, 32);
            const mercury_material = new THREE.MeshBasicMaterial({ map: mercury_texture });
            mercury = new THREE.Mesh(mercury_geometry, mercury_material);
            mercury.name = 'Mercury';
            scene.add(mercury);
        }
        posMercury.normalize().multiplyScalar(101);
        mercury.position.set(posMercury.x, posMercury.y, posMercury.z);
        mercury.layers.set(Layers.SCD.PLANETS);

        let mercuryLabel;

        if (scene.getObjectByName('Label_Mercury')){
            mercuryLabel = scene.getObjectByName('Label_Mercury');
        }
        else
        {
            mercuryLabel = HUDService.createPlanetLabel('Mercury', scene);
        }
        mercuryLabel.position.copy(posMercury.normalize().multiplyScalar(100));
        mercuryLabel.fontSize = SkychartService.getSettings().displaySettings.planetNamesSize.value;
        mercuryLabel.color = SkychartService.getSettings().displaySettings.planetNames.color;
        mercuryLabel.layers.set(Layers.SCD.PLANET_NAMES);

        if (mercuryLabel)
            mercuryLabel.lookAt(camera.position);
                             
            
    },
    buildStarlightAffected(objectData) {
        if (!objectData) return;
        objectData.starlightAffected = [];
        if (!objectData.finetrial) objectData.finetrial = [];
        for (const trial of objectData.finetrial) {
            if (trial[3] === '1') {
                objectData.starlightAffected.push(trial[0]);
            }
        }
        UtilsService.arrayReducer(objectData.finetrial, 2);  
    },
    buildBackgroundObjects(scene, data, mag, name = 'BackgroundObjects') {
        const map = SkychartService.textureLoader.load('/static/textures/star.png');
        const material = new THREE.SpriteMaterial({ alphaMap: map, alphaTest: 0.1, transparent: true });
        const [date, observatory, MST] = SkychartService.datum();
        
        const group = new THREE.Group();
        group.name = name;

        for (const object of data) {
            const rightAscension = object[1] * 1;
            const declination = object[2] * 1;
            const magnitude = object[3] * 1;
            
            if (magnitude != 'NULL' && magnitude <= mag) {
                let visibilityFactor = 0.1;
                if (magnitude < 2.5) {
                    visibilityFactor = 12;
                } else if (magnitude < 5) {
                    visibilityFactor = 6;
                } else if (magnitude < 7.5) {
                    visibilityFactor = 4;
                } else if (magnitude < 10) {
                    visibilityFactor = 2;
                } else if (magnitude < 12.5) {
                    visibilityFactor = 0.5;
                } else if (magnitude < 15) {
                    visibilityFactor = 0.3;
                } else if (magnitude < 17.5) {
                    visibilityFactor = 0.2;
                }
                let size = visibilityFactor / (magnitude + 3);

                const dec = declination;
                const ra = rightAscension;
                const observatoryCoordinates = [observatory.latitude, observatory.longitude];
                const [alt, az] = UtilsService.convertToAltitudeAndAzimuth(date, [ra, dec], observatoryCoordinates);

                const star = new THREE.Sprite(material);
                star.scale.set(size, size, size);
                star.objectInfo = object;
                star.position.setFromSphericalCoords(105, THREE.MathUtils.degToRad(90 - dec), THREE.MathUtils.degToRad(ra - 180));
                star.skyCoordinates = {ra, dec, alt, az};
                group.add(star);
            }
        }

        group.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
        group.rotation.y = THREE.MathUtils.degToRad(-MST);
        scene.add(group);
    },
    moveObjects(scene) {
        const [date, observatory, MST] = SkychartService.datum();
        scene.children.forEach(child => {
            if (child.name === 'Atmosphere') {
                const sunPosition = SunCalc.getPosition(date, observatory.latitude, observatory.longitude);
                const sunAlt = sunPosition.altitude;
                const sunAz = sunPosition.azimuth; 
                const sun = new THREE.Vector3();

                sun.setFromSphericalCoords(1, Math.PI/2 - sunAlt, Math.PI - sunAz);
                child.material.uniforms['sunPosition'].value.copy(sun);

                let sunHeight = UtilsService.normalize(0, Math.PI/2)(sunAlt) * 2;
                sunHeight = sunHeight < 0.1 ? 0.1 : sunHeight;
                sunHeight = sunHeight > 1 ? 1 : sunHeight;
                const dimmColor = UtilsService.rgbToHex((255 * sunHeight).toFixed());
                if (scene.getObjectByName('Landscape')) {
                    scene.getObjectByName('Landscape').material.color.setHex('0x'+dimmColor);
                }
            }
            
            if (
                    child.name === 'EquatorialGrid' ||
                    child.name === 'EclipticGrid' || 
                    child.name === 'BackgroundObjects' || 
                    child.name === 'BackgroundObjectsHighResolution' ||
                    child.name.indexOf('object_') === 0 ||
                    child.name.indexOf('UncertaintyRegion_') === 0
                ) {
                child.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
                child.rotation.y = THREE.MathUtils.degToRad(-MST);
            }
            if (child.name === 'MilkyWay') {               
                child.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
                child.rotation.y = THREE.MathUtils.degToRad(-MST - 90);
            }
            if (child.name === 'Constellations' || child.name === 'Regions' || child.name === 'Dsos') {               
                child.rotation.x = THREE.MathUtils.degToRad(90 - observatory.latitude);
                child.rotation.y = THREE.MathUtils.degToRad(-MST - 180);
            }
        })
    },
    getStarlightAffected(name, jd) {
        const julianDate = (jd * 1).toFixed(6);
        const starlightAffected = SkychartService.getTraceObjects()[name].starlightAffected;
        return starlightAffected.indexOf(julianDate) > -1;
    },
    getMoonlightAffected(name, jd) {
        const julianDate = (jd * 1).toFixed(6);
        const moonlightAffected = SkychartService.getTraceObjects()[name].moonlightAffected;
        return moonlightAffected.indexOf(julianDate) > -1;
    },
    interpolate(x, xValues, yValues) {
        if (x <= xValues[0]) return yValues[0];
        if (x >= xValues[xValues.length - 1]) return yValues[yValues.length - 1];
      
        for (let i = 0; i < xValues.length - 1; i++) {
          const x0 = xValues[i];
          const x1 = xValues[i + 1];
      
          if (x >= x0 && x <= x1) {
            const y0 = yValues[i];
            const y1 = yValues[i + 1];
            const t = (x - x0) / (x1 - x0);
            return y0 + (y1 - y0) * t;
          }
        }
      
        // Just in case — shouldn't hit this
        return null;
    },      
    buildTraceObject(scene, objectData, name, fov) {
        const syntheticObjects = SyntheticObjectsService.getSyntheticObjectsList();
        const syntheticObject = UtilsService.findItemInObjectList('designator', name, syntheticObjects);
        while (scene.getObjectByName('object_' + name))
            scene.remove(scene.getObjectByName('object_' + name))


        const id = MemoryService.encrypt(name).replace(/=/g,'');
        if (document.querySelector('#'+id)) {
            document.querySelector('#'+id).remove();
        }

        const group = new THREE.Group();
        group.name = 'object_' + name;        
        const uncertaintyGroup = new THREE.Group();
        uncertaintyGroup.name = 'UncertaintyRegion_' + name;        
        uncertaintyGroup.visible = SkychartService.getSettings().displaySettings.uncertaintyRegion.value;        
       
        const moonAndStarlightEnabled = SkychartService.getSettings().displaySettings.moonAndStarlight.value;
        const trailPointSymbol = SkychartService.getTrailPointSymbol();
        const trailPointColor = SkychartService.getSettings().displaySettings.trailPointSymbol.color;
        const trajectoryPointColor = SkychartService.getSettings().displaySettings.objectTrajectories.color; // red
        const [pointSize, trailSize] = SkychartService.getTrailPointSize(fov);
        const [date, observatory] = SkychartService.datum();
        const observatoryCoordinates = [observatory.latitude, observatory.longitude];

        const mapCircle = SkychartService.textureLoader.load('/static/textures/marker-disc.png');

        objectData.moonlightAffected = [];
        let trialCounter = 0;
        if (!objectData.finetrial) objectData.finetrial = [];
        for (const [index, trial] of objectData.finetrial.entries()) {
            const finetrialJd = parseFloat(trial[0]);
            const trailObjectJd = parseFloat(objectData.data[0].data[trialCounter][0]);
            const moonglow = parseFloat(objectData.data[0].data[trialCounter][23]);
            const deltaJd = trailObjectJd - finetrialJd;
            let finetrialStep;
            if (index + 1 < objectData.finetrial.length)
                finetrialStep = objectData.finetrial[index + 1][0] - finetrialJd;
            else 
                finetrialStep = objectData.finetrial[index - 1][0] - finetrialJd;

            if (deltaJd < finetrialStep)
                trialCounter = trialCounter + 1;

            const ra = trial[1];
            const dec = trial[2];

            const [alt, az] = UtilsService.convertToAltitudeAndAzimuth(date, [ra, dec], observatoryCoordinates);            

            let color = trajectoryPointColor; // red smooth
            let colorMoon = trajectoryPointColor; // red smooth
            let colorStars = trajectoryPointColor; // red smooth

            if (moonAndStarlightEnabled) {
                if (trial[3] === '1') {                    
                    colorStars = 0xFF0000; // red
                } else {
                    if (index && index < objectData.finetrial.length - 1) {
                        if (objectData.finetrial[index+1][3] === '1' || objectData.finetrial[index-1][3] === '1') {
                            colorStars = 0xFFF000; // yellow
                        } else {
                            colorStars = 0x00FF00; // green
                        }
                    }
                }

                if (moonglow < -3){
                    colorMoon = 0xFF0000; // red 
                }
                else if (moonglow < -1.6){
                    colorMoon = 0xFFF000; // yellow
                } else {
                    colorMoon = 0x00FF00; // green
                }

                if (colorMoon == 0xFF0000 || colorStars == 0xFF0000){
                    color = 0xFF0000; // red 
                } else if (colorMoon == 0xFFF000 || colorStars == 0xFFF000){
                    color = 0xFFF000; // yellow
                } else {
                    color = 0x00FF00; // green
                }
            }

            if (moonglow < -1.6)
                objectData.moonlightAffected.push(trial[0]);

            const material = new THREE.SpriteMaterial({ alphaMap: mapCircle, color:color, alphaTest: 0.1, transparent: true });
            const point = new THREE.Sprite(material);
            point.skyCoordinates = {ra, dec, alt, az};
            point.name = name+'_trail';
            point.scale.set(trailSize, trailSize, trailSize);
            point.position.setFromSphericalCoords(100, THREE.MathUtils.degToRad(90 - dec), THREE.MathUtils.degToRad(ra - 180));
            group.add(point);
        }

        const mapTrailPointSymbol = SkychartService.textureLoader.load('/static/textures/marker-' + trailPointSymbol + '.png');        
        if (!objectData.data[0].data) objectData.data[0].data = [];
        for (const [index, timestep] of objectData.data[0].data.entries()) {
            const ra = timestep[1] * 1;
            const dec = timestep[3] * 1;
            const material = new THREE.SpriteMaterial({ alphaMap: mapTrailPointSymbol, color:trailPointColor, alphaTest: 0.1, transparent: true });
            const object = new THREE.Sprite(material);
            object.objectInfo = [syntheticObject ? syntheticObject.objectName : name, ra, dec, timestep[10], timestep[0]];
            object.scale.set(pointSize, pointSize, pointSize);

            const [alt, az] = UtilsService.convertToAltitudeAndAzimuth(date, [ra, dec], observatoryCoordinates);
            object.position.setFromSphericalCoords(98, THREE.MathUtils.degToRad(90 - dec), THREE.MathUtils.degToRad(ra - 180));
            object.skyCoordinates = {ra, dec, az, alt};
            object.name = name;

            group.add(object);

            const timestamp = UtilsService.julianToTimestamp(timestep[0] * 1);
            const trackDescriptionLabel = new Text();
            trackDescriptionLabel.text = UtilsService.momentUtcDate(timestamp, true) + '   `';
            trackDescriptionLabel.defaultFontSize = 1.75;
            trackDescriptionLabel.fontSize = 1.75;
            trackDescriptionLabel.name = 'SkychartLabel_trackDescription';
            trackDescriptionLabel.anchorX = 'right';
            trackDescriptionLabel.anchorY = 'top';
            trackDescriptionLabel.layers.set(Layers.SCD.TRACK_DESCRIPTION);

            trackDescriptionLabel.position.setFromSphericalCoords(100, THREE.MathUtils.degToRad(90 - dec), THREE.MathUtils.degToRad(ra - 180));
            group.add(trackDescriptionLabel);

            if (!index) {
                const objectLabel = new Text();
                objectLabel.text = '   ' + (syntheticObject ? syntheticObject.objectName : name);
                //objectLabel.defaultFontSize = 2.25;
                //objectLabel.fontSize = 2.25;
                objectLabel.defaultFontSize = SkychartService.getSettings().displaySettings.objectNamesSize.value;
                objectLabel.fontSize = SkychartService.getSettings().displaySettings.objectNamesSize.value;
                objectLabel.name = 'SkychartLabel_object';
                objectLabel.anchorY = 'bottom';
                objectLabel.color = SkychartService.getSettings().displaySettings.objectNames.color;
                objectLabel.layers.set(Layers.SCD.OBJECT_NAMES);

                objectLabel.position.setFromSphericalCoords(100, THREE.MathUtils.degToRad(90 - dec), THREE.MathUtils.degToRad(ra - 180));
                group.add(objectLabel);
            }

            // =>> Uncertainty regions  <<=
            const err_a = timestep[10] * 1; // arcsec
            const err_b = timestep[11] * 1; // arcsec
            const pa = timestep[12] * 1; // degrees
            
             // The information we have from Aegis is err_a, err_b and AngAx where AngAx is the angle between semimajor axis (SA) 
             // and DEC measured counterclockwise starting from DEC. In our scene, the angle that we need to give in input is the angle 
             // between the X axis and SA (the green angle in the figure below). So, when we plot the ellipse we need to apply the correction 90 + AngAx
            const inclinationRad = THREE.MathUtils.degToRad(90 + pa); // inclination
            const arcsedeg = 0.000277778;
            const path = new THREE.Shape();
            if(err_b != 0. )
            {          
                path.absellipse(0,0,err_a*arcsedeg,err_b*arcsedeg,0, Math.PI*2, false, inclinationRad);
            }
            else
            {
                path.absellipse(0,0,err_a*arcsedeg,0.01*arcsedeg,0, Math.PI*2, false, inclinationRad);
            }

            const geometry = new THREE.ShapeBufferGeometry( path );             
            const ellipsoidMaterial = new THREE.MeshBasicMaterial({ 
                color: SkychartService.getSettings().displaySettings.uncertaintyRegion.color,
                wireframe: false,
                side: THREE.DoubleSide,
                transparent: true,
                opacity: .75
            });            
            const ellipse = new THREE.Mesh( geometry, ellipsoidMaterial );  

            ellipse.position.setFromSphericalCoords(100, THREE.MathUtils.degToRad(90 - dec), THREE.MathUtils.degToRad(ra - 180)); 
            let camera = new THREE.PerspectiveCamera(fov, window.innerWidth / window.innerHeight, 1, 2000);
            ellipse.lookAt(camera.position);
            ellipse.visible = true;            
            uncertaintyGroup.add(ellipse);

        }
        
        const traceObjects = SkychartService.getTraceObjects();
        traceObjects[name] = objectData;
        SkychartService.setTraceObjects(traceObjects);
        SkychartService.addLoadedObject(name);
        if ((SkychartService.getTraceDate()+'').length < 12) {
            if (objectData.data[0].data.length) {
                SkychartService.setTraceDate(objectData.data[0].data[0][0]);
            }   
        }
        scene.add(group);
        // Uncertainty regions settings
        scene.add(uncertaintyGroup);

        //var axesHelper = new THREE.AxesHelper( 5 );
        //scene.add( axesHelper );

        SkychartService.updateUncertaintyDensity(scene, name, SkychartService.getSettings().displaySettings.uncertaintyDensity.value);        
        SkychartService.updateUncertaintyFactor(scene, name, SkychartService.getSettings().displaySettings.uncertaintyFactor.value);        
        
        SkychartService.moveObjects(scene);
    },
    scaleTrailPoints(scene, objects, fov) {
        const [pointSize, trailSize] = SkychartService.getTrailPointSize(fov);
        const trailPoints = [];
        const fineTrailPoints = [];

        for (const object of objects) {
            scene.traverse(function(child) {  
                if (object.designator ? child.name === object.designator : child.name === object.name) {
                    trailPoints.push(child);
                }
                if (object.designator ? (child.name === object.designator + '_trail') : (child.name === object.name + '_trail')) {
                    fineTrailPoints.push(child);
                }
            });
        }

        for (const trailPoint of trailPoints) {
            trailPoint.scale.set(pointSize, pointSize, pointSize);
        }

        for (const fineTrail of fineTrailPoints) {
            fineTrail.scale.set(trailSize, trailSize, trailSize);
        }
    },    
    getLoadedObjects() {
        return store.getters[CommonGetters.skychartLoadedObjects];
    },
    getExceedPathAngleObjects() {
        return store.getters[CommonGetters.skychartExceedPathAngleObjects];
    },
    addLoadedObject(name) {
        const loadedObjects = SkychartService.getLoadedObjects();
        if (!loadedObjects.includes(name)) {
            loadedObjects.push(name)
        }
        SkychartService.setLoadedObjects(loadedObjects);
    },
    addExceedPathAngleObject(name) {
        const exceedPathAngleObjects = SkychartService.getExceedPathAngleObjects();
        if (!exceedPathAngleObjects.includes(name)) {
            exceedPathAngleObjects.push(name)
        }
        SkychartService.setExceedPathAngleObjects(exceedPathAngleObjects);                
    },
    setLoadedObjects(array) {
        store.dispatch(CommonActions.setSkychartLoadedObjects, array);
    },
    setExceedPathAngleObjects(array) {
        store.dispatch(CommonActions.setSkychartExceedPathAngleObjects, array);        
    },
    getTraceObjects() {
        return store.getters[CommonGetters.skychartTraceObjects];
    },
    getTraceObjectsToRemove() {
        return store.getters[CommonGetters.skychartTraceObjectsToRemove];
    },
    setTraceObjects(object) {
        store.dispatch(CommonActions.setSkychartTraceObjects, object);
    },
    setTraceObjectsToRemove(object) {
        store.dispatch(CommonActions.setSkychartTraceObjectsToRemove, object);
    },
    getTraceDate() {
        return store.getters[CommonGetters.skychartTraceDate];
    },
    setTraceDate(julianDate) {
        store.dispatch(CommonActions.setSkychartTraceDate, julianDate);
    },
    getTrailPointSymbol() {        
        return SkychartService.getSettings().displaySettings.trailPointSymbol.value;
    },
    getTrailPointSize(fov) {        
        const size = SkychartService.getSettings().displaySettings.trailPointSize.value;
        let scale = 0;
        switch (size) {
            case 'small':
                scale = 0.25;
                break;
            case 'medium':
                scale = 0.33;
                break;
            case 'large':
                scale = 0.75;
                break;
            default:
                break;
        }

        const pointSize = scale * fov * 0.1;

        let trailSize = pointSize / 5;
        if (trailSize < 0.001) {
            trailSize = 0.001;
        }

        return [pointSize, trailSize];
    },

    export(printRequest, invert) {
        HUDService.toggleWatermarks(true);
        requestAnimationFrame(() => {
            html2canvas(document.querySelector('#skychart'), { scale: 1 }).then(canvas => {
                const imgData = canvas.toDataURL('image/png');
                SkychartService.printRequest(printRequest, imgData, invert);
            })
            HUDService.toggleWatermarks(false);
        });
    },

    downloadImage(imgData) {
        const link = document.createElement('a');
        link.download = `SkyChartDisplay_${new Date().toISOString()}.png`;
        link.href = imgData;
        link.click();
        link.remove();
    },

    addInformationLine(label, text) {
        return {
            margin: [0, 1],
            text: [
                {
                    text: label+': ',
                    style: { bold: true }
                },
                text,
            ]
        }
    },

    addChartHeader(text) {
        return {
            text,
            margin: [0, 10, 0, 10],
            alignment: 'center',
            style: { bold: true }
        }
    },

    getPositionAtTimeElliptical(jd, eph){
        // Eccentricity
        const e = eph.get('e');
    
        // Mean anomaly
        const ma = eph.get('ma', 'rad');
    
        // Calculate mean anomaly at jd
        const n = eph.get('n', 'rad');
        const epoch = eph.get('epoch');
        const d = jd - epoch;

        const M = ma + n * d;

        // Estimate eccentric and true anom using iterative approx
        let E0 = M;
        for (let count = 0; count < 100; count++) {
          const E1 = M + e * Math.sin(E0);
          const lastdiff = Math.abs(E1 - E0);
          E0 = E1;
    
          if (lastdiff < 0.0000001) {
            break;
          }
        }

        const E = E0;
        const v = 2 * Math.atan(Math.sqrt((1 + e) / (1 - e)) * Math.tan(E / 2));

        // Radius vector, in AU
        const a = eph.get('a');
        const r = (a * (1 - e * e)) / (1 + e * Math.cos(v));
    
        return this.vectorToHeliocentric(v, r, eph);
    },

    vectorToHeliocentric(v, r, eph) {
        
        // Inclination, Longitude of ascending node, Longitude of perihelion
        const i = eph.get('i');
        const o = eph.get('om');
        const p = eph.get('wBar');
    
        // Heliocentric coords
        const X = r * (Math.cos(o) * Math.cos(v + p - o) - Math.sin(o) * Math.sin(v + p - o) * Math.cos(i));
        const Y = r * (Math.sin(o) * Math.cos(v + p - o) + Math.cos(o) * Math.sin(v + p - o) * Math.cos(i));
        const Z = r * (Math.sin(v + p - o) * Math.sin(i));
    
        return new THREE.Vector3(X, Y, Z);
      },

    downloadPdf(imgData) {
        SpinnerService.show();

        const traceObjects = SkychartService.getTraceObjects();

        const docDefinition = {
            content: [],
            footer: {
                margin: 15,
                fontSize: 9,
                alignment: 'right',
                text: (new Date()).getFullYear() + ' ' + Config.attributionText,
            },
        };

        const startDate = UtilsService.momentUtcDate(TimeFrameService.getTimeFrame().start, true)
        const endDate = UtilsService.momentUtcDate(TimeFrameService.getTimeFrame().end, true)
        const observatory = ObservatoryService.getSelectedObservatory();
        const coordinates = observatory.latitude.toFixed(5) + ', ' + observatory.longitude.toFixed(5);
        const observatoryName = observatory.name + ' (' + observatory.code + ')';
        const finalAsteroidsList = ObjectsService.getFinalAsteroidsList();
        const syntheticObjectsList = SyntheticObjectsService.getSyntheticObjectsList();
        const objectsList = finalAsteroidsList.concat(syntheticObjectsList);

        const addHeader = () => {
            docDefinition.content.push(
                {   
                    margin: [0, 0, 0, 50],
                    columns: [
                        {
                            alignment: 'right',
                            svg: '<svg xmlns="http://www.w3.org/2000/svg" width="88.928" height="32" viewBox="0 0 88.928 32"><path d="M25.889,21.284" transform="translate(1.726 1.419)" fill="#aaa"/><g transform="translate(36.458 7.2)"><path d="M60.479,14.031c-1.373-.254-2.949-.305-3.762-1.219a1.512,1.512,0,0,1-.154-1.525,3.191,3.191,0,0,1,3.254-1.374,2.9,2.9,0,0,1,2.694,1.627,3.846,3.846,0,0,1,.305,1.323H66.58A5.7,5.7,0,0,0,65.1,8.9a8.7,8.7,0,0,0-7.318-1.983c-1.831.254-4.271,1.321-5.086,3.712a4.79,4.79,0,0,0,1.373,5.337c1.729,1.323,4.982,1.679,6.305,1.985,1.524.3,2.288.508,2.541,1.371A1.948,1.948,0,0,1,62.666,21c-.967,1.27-2.949,1.373-4.526.915-1.322-.405-1.882-1.475-2.135-2.9H52.041a5.823,5.823,0,0,0,1.067,3.306,6.458,6.458,0,0,0,4.626,2.847,10.642,10.642,0,0,0,6.406-.714,7.447,7.447,0,0,0,2.084-1.881,5.555,5.555,0,0,0,.253-5.794C65.359,14.845,62.818,14.438,60.479,14.031Z" transform="translate(-32.989 -6.747)" fill="#aaa"/><path d="M48.552,9.306a8.151,8.151,0,0,0-7.319-2.442,7.953,7.953,0,0,0-5.643,3.865,11.443,11.443,0,0,0-1.323,3.763c-.407,3.1.458,8.286,5.9,10.32a8.849,8.849,0,0,0,8.185-1.169,8.051,8.051,0,0,0,2.847-4.73H46.672a3.587,3.587,0,0,1-1.322,2.034,4.181,4.181,0,0,1-4.83-.2c-1.373-1.017-1.829-2.693-1.881-5.084,0-1.27.356-3.3,1.423-4.27a4.2,4.2,0,0,1,3.967-.814,3.71,3.71,0,0,1,2.491,3.558H40.113v3.2H51.2a12.111,12.111,0,0,0-.305-3.356A11.886,11.886,0,0,0,48.552,9.306Z" transform="translate(-34.18 -6.749)" fill="#aaa"/><path d="M83.837,20.426V14.935a22.451,22.451,0,0,0-.355-4.169c-.305-1.322-1.475-3.508-5.237-3.966-.7-.05-1.45-.05-2.2-.05h-.034a11.965,11.965,0,0,0-3.626.659A5.612,5.612,0,0,0,69.027,12.8h4.066a2.642,2.642,0,0,1,1.577-2.541A3.651,3.651,0,0,1,76.042,10v0a3.714,3.714,0,0,1,2.71.867,1.778,1.778,0,0,1,.46,1.728c-.255,1.068-1.424,1.423-3.2,1.678v.005c-.6.1-1.3.149-2.047.249-2.135.254-5.186,1.322-5.9,4.423a5.1,5.1,0,0,0,1.829,4.93,7.328,7.328,0,0,0,4.423,1.374,9.161,9.161,0,0,0,1.73-.153v-.009a8.437,8.437,0,0,0,3.572-1.822,5.624,5.624,0,0,0,.508,1.475H84.4A10.243,10.243,0,0,1,83.837,20.426Zm-4.576-.66a3.639,3.639,0,0,1-3.253,2.083v.006a3.222,3.222,0,0,1-.98.044,2.409,2.409,0,0,1-2.237-1.27,2.058,2.058,0,0,1,.1-1.983c.61-1.067,1.678-1.27,3.153-1.575v-.005a10.229,10.229,0,0,0,1.488-.3,4.043,4.043,0,0,0,1.931-.864A20.277,20.277,0,0,1,79.261,19.765Z" transform="translate(-31.927 -6.75)" fill="#aaa"/></g><path d="M21.837,26.2c-7.205-2.487-8.539-9.153-6.742-13.82A8.381,8.381,0,0,1,27.526,8.662c4.361,3.127,3.9,9.126,3.9,9.126H20.247v-3.2h6.486a3.055,3.055,0,0,0-.9-2.436,3.932,3.932,0,0,0-4.845-.794c-1.257.666-2.871,3.435-1.923,7.408.9,3.017,3.726,4.215,6.27,4.214h0c3.789,0,6.658-2.638,6.663-6.958a16,16,0,1,0-3.809,10.342A10.225,10.225,0,0,1,21.837,26.2ZM7.678,18.013A1.825,1.825,0,1,1,9.5,16.188,1.825,1.825,0,0,1,7.678,18.013Z" transform="translate(0 0)" fill="#aaa"/></svg>',
                            width: 100,
                            margin: [0, 10, 0, 0]
                        },
                        {
                            width: '*',
                            text: '',
                        }
                    ]
                }
            );
        }
        
        addHeader();

        docDefinition.content.push(SkychartService.addInformationLine(Keys.t.startDate, startDate));
        docDefinition.content.push(SkychartService.addInformationLine(Keys.t.endDate, endDate));

        if (observatory.code) {
            docDefinition.content.push(SkychartService.addInformationLine(Keys.t.observatory, observatoryName));
        }

        docDefinition.content.push(SkychartService.addInformationLine(Keys.t.centreCoordinates, coordinates));
        docDefinition.content.push(SkychartService.addChartHeader(''));

        docDefinition.content.push({    
            margin: [0, 0, 0, 50],
            alignment: 'center',
            image: imgData,
            fit: [500,500],
            pageBreak: 'after'
        })

        if (Object.keys(traceObjects).length) {
            for (const objectName in traceObjects) {
                const object = objectsList.filter(e => e.name ? e.name === objectName : e.designator === objectName)[0];
                if (!object) {
                    continue;
                }
                addHeader();
                const detailsTableBody = [];
                docDefinition.content.push(SkychartService.addInformationLine(Keys.t.name, objectName.toString().endsWith(SyntheticObjectsService.SYNTHETIC_OBJECT_SUFFIX) ? object.objectName : objectName));

                if (object.name) {
                    for (const property in object) {
                        if (property === 'filename') continue;
                        const propertyName = Keys.t[property] ? Keys.t[property] : property;
                        let propertyValue = '-';
                        if (object[property]) {
                            propertyValue = object[property].name ? object[property].name : object[property];
                        }
                        detailsTableBody.push([propertyName, propertyValue])
                    }
                }
                
                if (object.objectName) {
                    for (const property in object) {
                        if (property === 'designator' || property === 'objectName') continue;
                        detailsTableBody.push([Keys.t[property], object[property] || '-'])
                    }
                }

                const datastampsTableBody = [];
                const dateHeader = Keys.t.date;
                const raHeader = Keys.t.ra + ' [' + Keys.t.degrees + ']';
                const decHeader = Keys.t.dec + ' [' + Keys.t.degrees + ']';
                datastampsTableBody.push([dateHeader, raHeader, decHeader]);
                
                for (const item of traceObjects[objectName].data[0].data) {
                    const timestamp = UtilsService.julianToTimestamp(item[0] * 1);
                    const julianToDate = UtilsService.momentUtcDate(timestamp, true) || '-';
                    datastampsTableBody.push([julianToDate, item[1], item[3]]);
                }
    
                const detailsTable = {
                    widths: ['*', '*'],
                    body: detailsTableBody,
                }

                const datastampsTable = {
                    widths: ['*', '*', '*'],
                    body: datastampsTableBody,
                }

                docDefinition.content.push({ 
                    margin: [0, 0, 0, 50],
                    columns: [
                        { table: detailsTable },
                        { table: datastampsTable },
                    ],
                    columnGap: 10
                });

                docDefinition.content.push({    
                    text: '',
                    pageBreak: 'after'
                })
            }
        }

        docDefinition.content[docDefinition.content.length - 1].pageBreak = false;

        pdfMake.vfs = pdfFonts.pdfMake.vfs;
        pdfMake.createPdf(docDefinition).download(`SkyChartDisplay_${new Date().toISOString()}.pdf`);

        SpinnerService.hide();
    },

    getObjectTracking() {
        return store.getters[CommonGetters.skychartObjectTracking];
    },
   
    setObjectTracking(boolean) {
        store.dispatch(CommonActions.setSkychartObjectTracking, boolean);
    },
        
    getDmsFormat() {
        return store.getters[CommonGetters.skychartDmsFormat];
    },

    setDmsFormat(boolean) {
        store.dispatch(CommonActions.setSkychartDmsFormat, boolean);
    },

    getObjectsDetailsHide() {
        return store.getters[CommonGetters.skychartObjectsDetailsHide];
    },

    setObjectsDetailsHide(boolean) {
        store.dispatch(CommonActions.setSkychartObjectsDetailsHide, boolean);
    },

    setCameraTarget(controls, altitude, azimuth, ra = null, dec = null) {
        if (ra && dec) {
            const [date, observatory] = SkychartService.datum();
            const observatoryCoordinates = [observatory.latitude, observatory.longitude];
            const [alt, az] = UtilsService.convertToAltitudeAndAzimuth(date, [ra, dec], observatoryCoordinates);
            altitude = alt;
            azimuth = az;
        }

        controls.minPolarAngle = THREE.MathUtils.degToRad(90 + altitude);
        controls.maxPolarAngle = THREE.MathUtils.degToRad(90 + altitude);
        controls.minAzimuthAngle = THREE.MathUtils.degToRad(180 - azimuth);
        controls.maxAzimuthAngle = THREE.MathUtils.degToRad(180 - azimuth);
        controls.update();
        setTimeout(() => {
            controls.minPolarAngle = 0;
            controls.maxPolarAngle = Math.PI;
            controls.minAzimuthAngle = -Infinity;
            controls.maxAzimuthAngle = Infinity;
            controls.update();
        }, 50)
    },

    printRequest(printRequest, imgData, invert) {
        if (invert) {
            const invertImage = UtilsService.invertImage(imgData);
            invertImage.then(invertedImgData => {
                if (printRequest === 'image') {
                    SkychartService.downloadImage(invertedImgData);
                } else if (printRequest === 'PDF') {
                    SkychartService.downloadPdf(invertedImgData);
                }
            });                       
        } else {
            if (printRequest === 'image') {
                SkychartService.downloadImage(imgData);
            } else if (printRequest === 'PDF') {
                SkychartService.downloadPdf(imgData);
            }
        }
    },

    convertToRaAndDec(alt, az) {
        const [date, observatory] = SkychartService.datum();
        const observatoryCoordinates = [observatory.latitude, observatory.longitude];
        return UtilsService.convertToRaAndDec(date, [alt, az], observatoryCoordinates);
    },

    sphericalToCartesian(azimuth, altitude, radius) {
        const x = radius * Math.cos(THREE.MathUtils.degToRad(altitude)) * Math.sin(THREE.MathUtils.degToRad(azimuth));
        const y = radius * Math.cos(THREE.MathUtils.degToRad(altitude)) * Math.cos(THREE.MathUtils.degToRad(azimuth));
        const z = radius * Math.sin(THREE.MathUtils.degToRad(altitude));
        return new THREE.Vector3(x, y, z);
    },

    cartesianToSphericalgrid(vector) {
        const radius = vector.length();
        let azimuth = -THREE.MathUtils.radToDeg(Math.atan2(vector.x, vector.z));
        if (azimuth < 0) 
            azimuth = 360 + azimuth;
        const altitude = THREE.MathUtils.radToDeg(Math.asin(vector.y / radius));
        return [ azimuth, altitude, radius ];
    },

    cartesianToSpherical(vector) {
        const radius = vector.length();
        const azimuth = THREE.MathUtils.radToDeg(Math.atan2(vector.x, vector.y));
        const altitude = THREE.MathUtils.radToDeg(Math.asin(vector.z / radius));
        return [ azimuth, altitude, radius ];
    },

    rotateAngles(azimuth, altitude, rotMatrixA, rotMatrixB) {
        const cartesianCoords = this.sphericalToCartesian(azimuth, altitude, 1);
        const rotatedCoordsA = this.multiplyMatrixVector(rotMatrixA, cartesianCoords);
        const rotatedCoords = this.multiplyMatrixVector(rotMatrixB, rotatedCoordsA);
        const [ newAzimuth, newAltitude ] = this.cartesianToSpherical(rotatedCoords);
        return [ newAzimuth, newAltitude ];
    },

    zoomRaDec(controls) {
        const azimuthalAngle = THREE.MathUtils.radToDeg(-(controls.getAzimuthalAngle() - Math.PI));
        const polarAngle = THREE.MathUtils.radToDeg(controls.getPolarAngle() - Math.PI/2);
        const [ra, dec] = SkychartService.convertToRaAndDec(polarAngle, azimuthalAngle);
        return [ra, dec, azimuthalAngle, polarAngle];
    },

    crosshairToggle(element) {
        const displaySettings = SkychartService.getSettings().displaySettings;
        const crosshair = displaySettings.crosshair.value;
        element.style.background = crosshair ? `transparent url(/static/textures/crosshair.png) center center no-repeat` : '';
    },

    toggleUncertaintyRegion(scene, name, visible) {                
        if (scene.getObjectByName('UncertaintyRegion_' + name)) {            
                scene.getObjectByName('UncertaintyRegion_'+ name).visible = visible;            
        }        
    },

    updateUncertaintyDensity(scene, name, value) {        
        const uncertaintyRegions = scene.getObjectByName('UncertaintyRegion_' + name);
        if (!uncertaintyRegions) {
            return;
        }         
        
        const totalPoints = uncertaintyRegions.children.length;        
        if (value === totalPoints) {
            for (const mesh of uncertaintyRegions.children) {
                mesh.visible = true;
            }
            return;
        } 

        const visiblePositions = [];        
        const middlePoint = Math.round(totalPoints / 2);
        const step = totalPoints / (value + 1);        
        for (let i = 0; i < totalPoints; i = i + step) {
            const roundIndex = Math.round(i);
            if (roundIndex && roundIndex !== totalPoints) {
                visiblePositions.push(roundIndex);
            }
        }
        if (visiblePositions.indexOf(middlePoint) === -1) {
            visiblePositions[0] = middlePoint;
        }
        for (const [index, mesh] of uncertaintyRegions.children.entries()) {
            if (visiblePositions.indexOf(index) > -1) {
                mesh.visible = true;
            } else {
                mesh.visible = false;
            }
        }                
    },
    
    updateUncertaintyFactor(scene, name, value) {
                
        if (scene.getObjectByName('UncertaintyRegion_' + name)) {  
            const uncertaintyRegions = scene.getObjectByName('UncertaintyRegion_' + name);
            for (const mesh of uncertaintyRegions.children) {
                mesh.scale.set(value, value, value);
            }                  
        }                               
    },
    updateUncertaintyColor(scene, name, color) {        
        if (scene.getObjectByName('UncertaintyRegion_' + name)) {  
            const uncertaintyRegions = scene.getObjectByName('UncertaintyRegion_' + name);
            for (const mesh of uncertaintyRegions.children) {                
                mesh.material.color.setHex(Number('0x' + color.replace('#', '')));                                
            }                  
        }      
    },
    updateObjectNamesColor(scene, name, value) {
        if (scene.getObjectByName('object_' + name) && scene.getObjectByName('object_' + name).getObjectByName('SkychartLabel_object')) {              
            let objectLabel = scene.getObjectByName('object_' + name).getObjectByName('SkychartLabel_object');
            objectLabel.color = value;          
        }                      
    },
    updateObjectNamesSize(scene, name, value) {
        if (scene.getObjectByName('object_' + name) && scene.getObjectByName('object_' + name).getObjectByName('SkychartLabel_object')) {              
            let objectLabel = scene.getObjectByName('object_' + name).getObjectByName('SkychartLabel_object');            
            objectLabel.defaultFontSize = value;
            objectLabel.fontSize = value;
        }     
    }, 
    updatePlanetsNamesColor(scene, value) {
        const planetNames = ['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'];
        for (const idx in planetNames){
            const planetName = planetNames[idx];
            if (scene.getObjectByName('Label_' + planetName)) {              
                let planetLabel = scene.getObjectByName('Label_' + planetName);            
                planetLabel.color = value;
            } 
        }    
    },  
    updatePlanetsNamesSize(scene, value) {
        const planetNames = ['Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'];
        for (const idx in planetNames){
            const planetName = planetNames[idx];
            if (scene.getObjectByName('Label_' + planetName)) {              
                let planetLabel = scene.getObjectByName('Label_' + planetName);            
                planetLabel.defaultFontSize = value;
                planetLabel.fontSize = value;
            } 
        }    
    }, 
    updateMoonNameColor(scene, value) {
        const planetNames = ['Moon'];
        for (const idx in planetNames){
            const planetName = planetNames[idx];
            if (scene.getObjectByName('Label_' + planetName)) {              
                let planetLabel = scene.getObjectByName('Label_' + planetName);            
                planetLabel.color = value;
            } 
        }    
    },  
    updateMoonNameSize(scene, value) {
        const planetNames = ['Moon'];
        for (const idx in planetNames){
            const planetName = planetNames[idx];
            if (scene.getObjectByName('Label_' + planetName)) {              
                let planetLabel = scene.getObjectByName('Label_' + planetName);            
                planetLabel.defaultFontSize = value;
                planetLabel.fontSize = value;
            } 
        }    
    },  
}

export default SkychartService;

